// ==========================================================================
// React Query Hooks for User Data
// ==========================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUser, acceptTos, updateLocation, updateUserName } from '@/services/user.service';
import type { User, UpdateLocationRequest } from '@/types/user';
import { applyFSPBranding } from '@/stores/authStore';
import { weatherKeys } from '@/hooks/useWeather';
import { alertKeys } from '@/hooks/useAlert';
import { preAlertKeys } from '@/hooks/usePreAlertActions';
import i18n from '@/i18n';

// Query keys
export const userKeys = {
  all: ['user'] as const,
  detail: () => [...userKeys.all, 'detail'] as const,
};

/**
 * Hook to fetch current user data with caching
 */
export function useUser(enabled = true) {
  return useQuery({
    queryKey: userKeys.detail(),
    queryFn: async () => {
      const user = await getUser();
      
      // Apply locale
      if (user.locale) {
        i18n.changeLanguage(user.locale);
      }
      
      // Apply FSP branding
      if (user.fsp) {
        applyFSPBranding(user.fsp);
      }
      
      return user;
    },
    enabled: enabled && !!localStorage.getItem('access_token'),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401 (unauthorized)
      if ((error as { response?: { status?: number } })?.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Hook to accept Terms of Service
 */
export function useAcceptTos() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: acceptTos,
    onSuccess: (updatedUser) => {
      // Update cached user data immediately (if valid response)
      if (updatedUser && typeof updatedUser === 'object' && 'id' in updatedUser) {
        queryClient.setQueryData<User>(userKeys.detail(), updatedUser);
      }
    },
  });
}

/**
 * Hook to update/confirm location
 * Returns a promise that resolves when cache is updated
 */
export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateLocationRequest) => updateLocation(data),
    onSuccess: async (updatedUser) => {
      // Update cached user data immediately (if valid response)
      if (updatedUser && typeof updatedUser === 'object' && 'id' in updatedUser) {
        queryClient.setQueryData<User>(userKeys.detail(), updatedUser);
      } else {
        // If no valid response, force refetch to get fresh data
        await queryClient.invalidateQueries({ queryKey: userKeys.detail() });
      }
      // Weather, alerts and pre-alert actions are all location-scoped —
      // refetch them so the UI follows the newly saved location.
      queryClient.invalidateQueries({ queryKey: weatherKeys.all });
      queryClient.invalidateQueries({ queryKey: alertKeys.all });
      queryClient.invalidateQueries({ queryKey: preAlertKeys.all });
    },
  });
}

/**
 * Hook to update user name
 */
export function useUpdateUserName() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => updateUserName(name),
    onSuccess: (updatedUser) => {
      if (updatedUser && typeof updatedUser === 'object' && 'id' in updatedUser) {
        queryClient.setQueryData<User>(userKeys.detail(), updatedUser);
      }
    },
  });
}

/**
 * Hook to invalidate user cache (force refetch)
 */
export function useInvalidateUser() {
  const queryClient = useQueryClient();
  
  return () => {
    queryClient.invalidateQueries({ queryKey: userKeys.all });
  };
}

/**
 * Hook to clear user cache (on logout)
 */
export function useClearUserCache() {
  const queryClient = useQueryClient();
  
  return () => {
    queryClient.removeQueries({ queryKey: userKeys.all });
  };
}
